import { HTMLInputTypeAttribute } from 'react';

import { cn } from '~/lib/utils/classname';

export const defaultInputType = 'text' as const;
export type DefaultInputType = typeof defaultInputType;

type InputType = HTMLInputTypeAttribute | 'textarea';

type ClassNameSlots = 'main' | 'label' | 'input';

export type OwnProps<Type extends InputType> = {
  type?: Type;
  label?: string;
  className?: string;
  classNames?: { [key in ClassNameSlots]?: string };
};

export type Props<Type extends InputType> = OwnProps<Type> &
  Omit<
    Type extends HTMLInputTypeAttribute
      ? React.InputHTMLAttributes<HTMLInputElement>
      : React.TextareaHTMLAttributes<HTMLTextAreaElement>,
    keyof OwnProps<Type>
  >;

const FormInput = <Type extends InputType = DefaultInputType>({
  type,
  label,
  className,
  classNames,
  ...inputProps
}: Props<Type>) => {
  const inputType = type || defaultInputType;

  return (
    <label
      htmlFor={inputProps.name}
      className={cn(
        'flex w-full flex-col transition-opacity',
        {
          'pointer-events-none opacity-50': inputProps.disabled,
        },
        className,
        classNames?.main
      )}
    >
      {label && (
        <span
          className={cn(
            'text-brand-primary relative block px-6 py-2 text-base',
            classNames?.label
          )}
        >
          {label}
          {inputProps.required && <span className="text-danger">&nbsp;*</span>}
        </span>
      )}

      {inputType === 'textarea' ? (
        <textarea
          className={cn(
            'bg-brand-quaternary placeholder:text-brand-primary border-brand-primary h-48 resize-none border-b px-6 py-5 text-white outline-none',
            classNames?.input
          )}
          id={inputProps.id || inputProps.name}
          {...(inputProps as React.TextareaHTMLAttributes<HTMLTextAreaElement>)}
        />
      ) : (
        <input
          type={type}
          className={cn(
            'bg-brand-quaternary placeholder:text-brand-primary border-brand-primary h-16 border-b px-6 text-white outline-none',
            classNames?.input
          )}
          id={inputProps.id || inputProps.name}
          {...inputProps}
        />
      )}
    </label>
  );
};

export default FormInput;
